import { Box, Chip, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const StyledProgramTypeButtonStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isDisabled',
})<{ isSelected?: boolean; isDisabled?: boolean }>(
  ({ theme, isSelected = false, isDisabled = false }) => ({
    padding: theme.spacing(2, 0),
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected
      ? theme.palette.background.blue
      : isDisabled
        ? theme.palette.text.fifth
        : theme.palette.background.default,
    border: '1px solid',
    borderColor: isSelected
      ? theme.palette.info.main
      : isDisabled
        ? theme.palette.text.fourth
        : theme.palette.text.fifth,
    borderRadius: theme.customShape.borderRadiusFull,
    pointerEvents: isDisabled || isSelected ? 'none' : 'auto',
    width: '100%',
    height: 'auto',
    boxSizing: 'border-box',
    transition: 'borderColor 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    flexDirection: 'row',
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: 'transparent',
    },
  }),
);

export const StyledIconBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
}));

export const StyledDesignIconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled?: boolean }>(({ theme, isDisabled = false }) => ({
  width: 42,
  height: 49,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: '100%',
    height: '100%',
    viewBox: '0 0 42 49',
    fill: 'none',
    stroke: isDisabled
      ? theme.palette.text.secondary
      : theme.palette.text.primary,
    color: isDisabled
      ? theme.palette.text.secondary
      : theme.palette.text.primary,
  },
}));

export const StyledProgramTypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isDisabled',
})<{ isSelected?: boolean; isDisabled?: boolean }>(
  ({ theme, isSelected = false, isDisabled = false }) => ({
    marginLeft: theme.spacing(-3),
    marginTop: theme.spacing(4),
    zIndex: 1,
    borderWidth: '1.25px',
    borderColor: isDisabled
      ? theme.palette.text.secondary
      : theme.palette.secondaryInvert.main,
    height: 24,
    backgroundColor: isSelected
      ? theme.palette.background.default
      : isDisabled
        ? theme.palette.background.default
        : theme.palette.secondaryInvert.main,
    borderRadius: theme.customShape.borderRadiusFull,
    fontSize: theme.typography.pxToRem(14),
    pointerEvents: 'none',
    '& .MuiChip-label': {
      padding: theme.spacing(1, 4),
      color: isSelected
        ? theme.palette.text.primary
        : isDisabled
          ? theme.palette.text.secondary
          : theme.palette.common.white,
      fontWeight: 500,
    },
  }),
);

export const StyledTextContainerBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(3.5),
  whiteSpace: 'nowrap',
}));

export const StyledDividerBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled?: boolean }>(({ theme, isDisabled = false }) => ({
  borderLeftStyle: 'solid',
  borderColor: isDisabled ? theme.palette.text.fourth : theme.palette.info.main,
  height: theme.spacing(6),
  borderLeftWidth: 1,
  marginRight: theme.spacing(2),
}));

export const StyledTitleTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled?: boolean }>(({ theme, isDisabled = false }) => ({
  fontSize: theme.typography.pxToRem(16),
  color: isDisabled ? theme.palette.text.secondary : theme.palette.text.primary,
}));

export const StyledBadgeBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: theme.spacing(8),
  transform: 'translateY(-50%)',
  zIndex: 1,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.text.main,
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(8),
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1,
}));
